import { Button, Flex, Icon, notify } from '@wyz/ui';
import { useModalState } from '@wyz/utils';
import { isEmpty } from 'lodash-es';
import { Link } from 'react-router';
import { Trans, useConfig } from '~/common/context';
import { ROLES } from '~/common/helpers';
import {
  useAccessControl,
  useListLocationState,
  usePricingPolicyErrorTranslation,
} from '~/common/hooks';
import { useManageQuotes } from '~/purchase/api';
import { IQuote } from '~/purchase/types';
import { useState } from 'react';
import { PdfModal } from '~/common/components';
import { QuoteRecipientModal } from '~/purchase/components';

interface QuoteActionsProps {
  quote: IQuote;
}

export const QuoteActions = ({ quote }: QuoteActionsProps) => {
  const { apiUrl, tenantCode } = useConfig();
  const acl = useAccessControl();
  const listState = useListLocationState();
  const { resolveErrorKey } = usePricingPolicyErrorTranslation();

  const manageQuotes = useManageQuotes({
    resendMail: { enabled: true },
    duplicate: { enabled: true },
  });

  const [pdf, setPdf] = useState<string | null>(null);
  const [recipientModal, recipientModalHandlers] =
    useModalState<'EDIT_RECIPIENT'>();

  const recipient = quote.quoteEndCustomerData;
  // WCT-5218: with an address and consent on file the mail goes out straight away;
  // otherwise the button opens the recipient modal so the data can be completed first.
  const canSendDirectly = !isEmpty(recipient.email) && recipient.gdprAccepted;

  return (
    <Flex
      alignItems='center'
      justifyContent='center'
      className='space-x-2 '
      wrap='wrap'
    >
      <Button
        id={`resend-${quote.reference}`}
        variant='link'
        size='xs'
        isLoading={manageQuotes.resendMail.isLoading}
        onClick={() => {
          if (!canSendDirectly) {
            recipientModalHandlers.open('EDIT_RECIPIENT');

            return;
          }

          manageQuotes.resendMail.mutate(
            {
              customerId: quote.customerId,
              reference: quote.reference,
            },
            {
              onSuccess: () => {
                notify.success(
                  <Trans i18nKey='app.common.messages.success' />,
                  { id: 'toast-success-quote-resend-mail' },
                );
              },
              onError: (error) => {
                notify.error(
                  <Trans i18nKey={resolveErrorKey(error.message)} />,
                  { id: 'toast-error-quote-resend-mail' },
                );
              },
            },
          );
        }}
        disabled={
          !acl.isGranted([ROLES.ROLE_QUOTE_SEND_MAIL]) ||
          quote.status === 'DRAFT'
        }
      >
        <Flex className='space-x-2'>
          <Icon icon='marketing-fill' size='xxs' />
          <span>
            <Trans i18nKey='app.quote.history.table.sub_row.resend_mail' />
          </span>
        </Flex>
      </Button>

      <Button
        id={`print-${quote.reference}`}
        variant='link'
        size='xs'
        onClick={() => {
          setPdf(
            `${apiUrl}/offers/api/tenants/${tenantCode}/customers/${
              quote.customerId
            }/quotes/${quote.reference}/print`,
          );
        }}
      >
        <Flex className='space-x-2'>
          <Icon icon='eye' size='xxs' />
          <span>
            <Trans i18nKey='app.quote.diagnostics.table.quote.actions.pdf' />
          </span>
        </Flex>
      </Button>

      <Button
        id={`duplicate-${quote.reference}`}
        size='xs'
        variant='link'
        isLoading={manageQuotes.duplicate.isLoading}
        onClick={() => {
          manageQuotes.duplicate.mutate(
            {
              customerId: quote.customerId,
              reference: quote.reference,
            },
            {
              onSuccess: () => {
                notify.success(
                  <Trans i18nKey='app.common.messages.success' />,
                  { id: 'toast-success-quote-duplicate' },
                );
              },
              onError: (error) => {
                notify.error(
                  <Trans i18nKey={resolveErrorKey(error.message)} />,
                  { id: 'toast-error-quote-duplicate' },
                );
              },
            },
          );
        }}
        disabled={quote.status !== 'ENABLED'}
      >
        <Flex className='space-x-2'>
          <Icon icon='duplicate' size='xxs' />
          <span>
            <Trans i18nKey='app.common.button.duplicate' />
          </span>
        </Flex>
      </Button>

      {pdf !== null && <PdfModal onClose={() => setPdf(null)} url={pdf} />}

      {recipientModal === 'EDIT_RECIPIENT' && (
        <QuoteRecipientModal
          quote={quote}
          onClose={recipientModalHandlers.close}
        />
      )}

      {quote.status === 'DRAFT' && (
        <Button
          id={`edit-${quote.reference}`}
          size='xs'
          variant='link'
          as={Link}
          to={`/quotes/${quote.reference}/edit`}
          state={listState}
        >
          <Flex className='space-x-2'>
            <Icon icon='edit' size='xxs' />
            <span>
              <Trans i18nKey='app.purchase.quote.history.table.sub_row.edit' />
            </span>
          </Flex>
        </Button>
      )}
    </Flex>
  );
};
